import { useCallback, useEffect, useRef, useState } from 'react';
import type { IconName } from '@cencosud-cencommerce/paris-uikit';
import {
  IconButton,
  ProductThumbnail,
  QuantitySelector,
  SaleTag,
  Tag,
  Text,
  Tooltip
} from '@cencosud-cencommerce/paris-uikit';
import debounce from 'lodash/debounce';

import { useAppSelector } from '@/Application/Hooks/Redux';
import type { UpdateQuantityErrorDto } from '@/modules/cart/application/updateLineItemQuantity';
import type { CartLineItem } from '@/modules/cart/domain/CartLineItem';
import type { CartProductOption } from '@/modules/cart/domain/CartProductOption';
import environment from '@/modules/cart/environments/environment';
import { getProductMapForAddToCartEvent } from '@/modules/shared/utils/gtmEvents/events';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendShippingEvents';
import {
  EEventName,
  type ShippingEventKeys
} from '@/modules/shared/utils/gtmEvents/types';
import useUpdateLineItemQuantity from '@/sections/cart/hooks/mutators/useUpdateLineItemQuantity';

import type { ProductSamplingItemProps } from './ProductExtraList/ProductSamplingsList/ProductSamplingItem/ProductSamplingItem';
import type { ProductServiceItemProps } from './ProductExtraList/ProductServiceList/ProductServiceItem/ProductsServiceItem';
import { ProductExtraList } from './ProductExtraList';

const { MAX_QUANTITY_PER_PRODUCT } = environment;

export interface ProductItemProps {
  'data-testid'?: string;
  productId: string;
  productSKU?: string;
  name: string;
  imageUrl: string;
  soldTooltip?: {
    title?: string;
    text?: string;
  };
  saleTagProps?: {
    label: string;
    storeName: string;
    iconLeft?: IconName;
    iconRight?: IconName;
    className?: string;
    'data-testid'?: string;
  };
  isPreSale: boolean;
  quantity: number;
  onDelete: (productId: string) => void;
  specs?: { label: string; value: string }[];
  services?: ProductServiceItemProps[];
  samples?: ProductSamplingItemProps[];
  handleSetOptions?: () => void;
}

const ProductItem: React.FC<ProductItemProps> = ({
  'data-testid': dataTestId = 'paris-product-item',
  productId,
  productSKU,
  name,
  imageUrl,
  saleTagProps,
  soldTooltip,
  isPreSale,
  onDelete,
  specs,
  services,
  samples,
  quantity,
  handleSetOptions
}) => {
  const cart = useAppSelector((state) => state.cart.data);

  const [value, setValue] = useState(quantity);
  const [maxValue, setMaxValue] = useState(MAX_QUANTITY_PER_PRODUCT);
  const previousQuantity = useRef(quantity);
  const { trigger: updateLineItemQuantity, isLoading } =
    useUpdateLineItemQuantity();

  const maxValueLabel = `Máx. ${maxValue} unid`;

  useEffect(() => {
    setValue(quantity);
    previousQuantity.current = quantity;
  }, [quantity]);

  const sendGtmEventForAddOrRemove = (
    event: 'addToCart' | 'removeFromCart',
    product: CartLineItem,
    productOptions: CartProductOption[],
    quantity: number
  ) => {
    const eventName: ShippingEventKeys =
      event === 'addToCart'
        ? EEventName.ADD_TO_CART
        : EEventName.REMOVE_FROM_CART;

    sendEvent({
      event: eventName,
      extraFields: {
        ecommerce: {
          checkout: {
            products: [
              getProductMapForAddToCartEvent(product, quantity, productOptions)
            ]
          }
        }
      }
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateLineItemQuantityDebounced = useCallback(
    debounce(async (newQuantity: number) => {
      try {
        const res = await updateLineItemQuantity({
          lineItemId: productId,
          quantity: newQuantity
        });

        if (!res) {
          setValue(quantity);
        }

        if (res && handleSetOptions) {
          handleSetOptions();
          const updatedProduct = res?.lineItems?.find(
            (item) => item.id === productId
          );
          if (updatedProduct) {
            const event =
              newQuantity > previousQuantity.current
                ? 'addToCart'
                : 'removeFromCart';
            sendGtmEventForAddOrRemove(
              event,
              updatedProduct,
              res?.productOptions,
              newQuantity
            );
          }
          previousQuantity.current = newQuantity;
        }
      } catch (error) {
        const updateError = error as UpdateQuantityErrorDto;
        const availableQty = updateError?.error?.meta?.find(
          (meta) => meta.sku === productSKU
        )?.availableQty;
        if (availableQty) {
          setMaxValue(availableQty);
        }
        setValue(quantity);
      }
    }, 300),
    []
  );

  const handleRemoveItem = async () => {
    const productToRemove = cart?.lineItems?.find(
      (item) => item?.id === productId
    );

    const productOptions =
      cart?.productOptions?.filter(
        (option) => option?.productSku === productSKU
      ) ?? [];

    if (productToRemove) {
      sendGtmEventForAddOrRemove(
        'removeFromCart',
        productToRemove,
        productOptions,
        quantity
      );
    }
    onDelete(productId);
  };

  const handleQuantityChange = async (newQuantity: number) => {
    setValue(newQuantity);
    await updateLineItemQuantityDebounced(newQuantity);
  };

  const SoldBy = () => {
    if (!saleTagProps) return null;

    if (soldTooltip?.text) {
      return (
        <Tooltip title={soldTooltip?.title} content={soldTooltip?.text}>
          <SaleTag {...saleTagProps} iconRight="info-circle" />
        </Tooltip>
      );
    }

    return <SaleTag {...saleTagProps} />;
  };

  return (
    <div className="co-flex co-flex-col">
      <div data-testid={dataTestId} className="co-flex co-gap-3 co-p-3">
        <ProductThumbnail src={imageUrl} />
        <div className="co-grid co-grid-cols co-w-full co-gap-3">
          <div className="co-flex co-justify-between co-gap-3 co-min-w-0 co-h-[45px]">
            <div className="co-flex co-flex-col co-min-w-0 co-gap-1">
              <div className="co-overflow-hidden co-text-ellipsis co-whitespace-nowrap co-flex-shrink-0">
                <Text variant="body2" weight={600}>
                  {name}
                </Text>
              </div>
              {saleTagProps && !isPreSale && (
                <div>
                  <SoldBy />
                </div>
              )}
              {isPreSale && (
                <div>
                  <Tag variant="information" iconName="order">
                    Preventa
                  </Tag>
                </div>
              )}
            </div>
            <div className="co-my-auto">
              <IconButton
                data-testid="paris-delete-item"
                size="sm"
                icon="delete"
                feedback="neutral"
                onClick={handleRemoveItem}
              />
            </div>
          </div>
          <div className="co-flex co-justify-between co-min-w-0 ">
            <div className="co-flex co-flex-col co-min-w-0">
              {specs &&
                specs.map((spec) => (
                  <div
                    key={spec.value}
                    className="co-overflow-hidden co-text-ellipsis co-whitespace-nowrap"
                  >
                    <Text className="co-me-1" variant="caption" weight={600}>
                      {spec.label}:
                    </Text>
                    <Text variant="caption" weight={400}>
                      {spec.value}
                    </Text>
                  </div>
                ))}
            </div>
            <div className="co-flex co-flex-col co-justify-center co-items-center co-gap-[4px]">
              <QuantitySelector
                value={value}
                onChange={handleQuantityChange}
                disabled={isLoading}
                min={1}
                max={maxValue}
              />
              {value === maxValue ? (
                <Text variant="caption" weight={500} color="neutral-800">
                  {maxValueLabel}
                </Text>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {(services || samples) && (
        <ProductExtraList samples={samples} services={services} />
      )}
    </div>
  );
};

export default ProductItem;
